import type {
  InteractionBlueprint,
  SparxListResponse,
} from "@exagens/behavioral-insight-console-client";
import type { SparxEditorApi } from "@exagens/behavioral-insight-console-client/dist/apis/SparxEditorApi";
import {
  defer,
  from,
  fromEvent,
  map,
  mergeMap,
  takeUntil,
  throwError,
  type Observable,
} from "rxjs";
import { consoleFetch } from "@/lib/http/consoleFetch";
import { getSparxEditorApi } from "@/services/consoleApiLoaders";
import { getPublicRuntimeConfig } from "@/store/app/publicRuntimeConfigInstance";
import { parseInteractionBlueprint } from "@/lib/contentParsers/interactionBlueprint";
import { parseSparxsEditorListResponse } from "@/lib/contentParsers/sparxsEditorList";
import { parseSparxReactionsListResponse } from "@/lib/sparxs/sparxsReactionsSchemas";
import type { SparxReactionInfo } from "@/lib/sparxs/sparxsReactionsSchemas";
import type { SparxsBlueprintFetchResult } from "@/types/sparxsBlueprintFetchResult";
import { retryOnUnauthorized } from "@/store/consoleApi/retryOnUnauthorized";

/** Required header param on the generated client; the Bearer middleware overwrites it. */
const AUTH_HEADER_PLACEHOLDER = "bearer-set-by-middleware";

const sparxEditorAuthHeader = () => ({
  authorization: AUTH_HEADER_PLACEHOLDER,
});

export const displayAllInteraction = (): Observable<SparxListResponse> => {
  return from(getSparxEditorApi()).pipe(
    mergeMap((api) =>
      api
        .listSparxBlueprints(sparxEditorAuthHeader())
        .pipe(map((data) => parseSparxsEditorListResponse(data))),
    ),
    retryOnUnauthorized(),
  );
};

export const listSparxReactions$ = (): Observable<
  Record<string, SparxReactionInfo[]>
> => {
  return from(
    consoleFetch(getPublicRuntimeConfig(), "/api/sparxseditor/reactions").then(
      async (response) => {
        if (!response.ok) {
          throw new Error(
            `GET /api/sparxseditor/reactions failed (${response.status})`,
          );
        }
        return response.json() as Promise<unknown>;
      },
    ),
  ).pipe(
    map((data) => {
      const catalogs = parseSparxReactionsListResponse(data);
      return Object.fromEntries(
        catalogs.map((catalog) => [catalog.version, catalog.reactions]),
      );
    }),
  );
};

export type SaveSparxResult = {
  userMessage: string;
  files: { fileName: string; lastModified: number }[];
};

export const parseSparxSaveResponseBody = (raw: string): SaveSparxResult => ({
  userMessage: String(raw ?? ""),
  files: [],
});

export const saveSparx = (
  sparxs: InteractionBlueprint[],
  message: string,
): Observable<SaveSparxResult> => {
  return from(getSparxEditorApi()).pipe(
    mergeMap((api) =>
      api
        .saveSparxBlueprint({
          ...sparxEditorAuthHeader(),
          saveRequest: {
            sparxs,
            message,
          },
        })
        .pipe(map((raw) => parseSparxSaveResponseBody(String(raw ?? "")))),
    ),
    retryOnUnauthorized(),
  );
};

const mapSparxBlueprintWireToFetchResult = (
  raw: unknown,
): SparxsBlueprintFetchResult => {
  if (raw == null || typeof raw !== "object") {
    throw new Error("Empty response");
  }
  const rec = raw as Record<string, unknown>;
  const { lastModified, ...rest } = rec;
  const blueprint = parseInteractionBlueprint(rest);
  const lastModifiedMs =
    typeof lastModified === "number" && Number.isFinite(lastModified)
      ? lastModified
      : undefined;
  return { blueprint, lastModified: lastModifiedMs };
};

export const getSparxStaticImage$ = (imageName: string): Observable<Blob> =>
  from(getSparxEditorApi()).pipe(
    mergeMap((api: SparxEditorApi) =>
      api.getSparxImage({
        ...sparxEditorAuthHeader(),
        imageName,
      }),
    ),
    retryOnUnauthorized(),
  );

export const getSparxBlueprint$ = (
  fileName: string,
  init?: RequestInit,
): Observable<SparxsBlueprintFetchResult> => {
  return defer(() => {
    const signal = init?.signal;
    if (signal != null && signal.aborted) {
      return throwError(() => new DOMException("Aborted", "AbortError"));
    }

    const request$ = from(getSparxEditorApi()).pipe(
      mergeMap((api) =>
        api
          .getSparxBlueprint({
            ...sparxEditorAuthHeader(),
            fileName,
          })
          .pipe(
            map((response) => mapSparxBlueprintWireToFetchResult(response)),
          ),
      ),
      retryOnUnauthorized(),
    );

    if (signal == null) {
      return request$;
    }

    return request$.pipe(takeUntil(fromEvent(signal, "abort")));
  });
};
